import {
  asExternalAccountID,
  type ExternalAccountID,
} from "@repo/db/branded-types";
import { NextRequest, NextResponse } from "next/server";
import { EXTERNAL_ACCOUNT_ID_COOKIE_NAME } from "./constants";

/**
 * Resolve the external account ID from the request: URL query param → cookie.
 * The URL param takes priority so that a new account link overrides a stale cookie.
 * Returns undefined if neither source provides the ID.
 *
 * @param req - Incoming NextRequest object
 * @returns The resolved external account ID and whether it was newly set, or undefined
 */
export function resolveExternalAccountId(
  req: NextRequest
): { externalAccountId: ExternalAccountID; isNew: boolean } | undefined {
  const existing = req.cookies.get(EXTERNAL_ACCOUNT_ID_COOKIE_NAME)?.value;
  const fromUrl = req.nextUrl.searchParams.get("externalAccountId");

  // 1. URL param overrides a stale cookie (e.g. after logout + new account creation)
  if (fromUrl && fromUrl !== existing) {
    req.cookies.set(EXTERNAL_ACCOUNT_ID_COOKIE_NAME, fromUrl);
    return { externalAccountId: asExternalAccountID(fromUrl), isNew: true };
  }

  // 2. Cookie matches or no URL param — use existing cookie
  if (existing) {
    return { externalAccountId: asExternalAccountID(existing), isNew: false };
  }

  // 3. URL param present and matches cookie (already handled above as existing)
  if (fromUrl) {
    req.cookies.set(EXTERNAL_ACCOUNT_ID_COOKIE_NAME, fromUrl);
    return { externalAccountId: asExternalAccountID(fromUrl), isNew: true };
  }

  // 4. Neither source has the ID
  return undefined;
}

/** Persist the external-account-id cookie on the outgoing response (1-year expiry). */
export function setExternalAccountIdCookie(
  res: NextResponse,
  externalAccountId: ExternalAccountID
): void {
  res.cookies.set(EXTERNAL_ACCOUNT_ID_COOKIE_NAME, externalAccountId, {
    maxAge: 60 * 60 * 24 * 365,
    httpOnly: true,
    sameSite: "lax",
  });
}

/**
 * Read the external-account-id cookie from a server component / action.
 * Returns undefined for unauthenticated visitors (e.g. before the Owlet
 * token-sync handoff has set the cookie).
 */
export async function getExternalAccountId(): Promise<
  ExternalAccountID | undefined
> {
  const { cookies } = await import("next/headers");
  const cookieStore = await cookies();
  const value = cookieStore.get(EXTERNAL_ACCOUNT_ID_COOKIE_NAME)?.value;
  return value ? asExternalAccountID(value) : undefined;
}
